import { ApiClientBase } from '@@/api/api';
import { ApiResult } from '@@/types/ApiResult';
import { Channel, Contact, ContactInitiationMethod, SearchContactsTimeRangeType } from '@aws-sdk/client-connect';
import { InteractionLogsObject } from '../flowVisualiser/types/interaction-log-data';
import { FlowLogEvent } from '../flowVisualiser/types/flow-log';

export interface BaseRequestPayload {
  instanceId: string;
  region: string;
}

export interface CallJsonResponse {
  [key: string]: unknown;
}

export interface SearchContactsItem {
  id: string;
  initiationTimestamp: string;
  disconnectTimestamp: string | null;
  channel: Channel | null;
  isPending: boolean;
  agent: {
    id: string;
    connectedAt: string;
  } | null;
  initiationMethod: ContactInitiationMethod | null;
}

export interface SearchContactsRequest {
  contactId?: string;
  agentIds?: string[];
  initiationMethods?: ContactInitiationMethod[];
  queueIds?: string[];
  includePending?: boolean;
  channels?: string[];
  timeRange: {
    startTime: string;
    endTime: string;
    type: SearchContactsTimeRangeType;
  };
  segmentAttributes?: {
    criteria: {
      Key: string;
      Values: string[]; // max 20 items
    }[]; // max 15 items
    matchType: 'MATCH_ALL' | 'MATCH_ANY' | 'MATCH_EXACT' | 'MATCH_NONE';
  };
  contactAttributes?: {
    criteria: {
      Key: string;
      Values: string[]; // max 20 items
    }[]; // max 15 items
    matchType: 'MATCH_ALL' | 'MATCH_ANY' | 'MATCH_EXACT' | 'MATCH_NONE';
  };
}

export interface SearchContactsResult {
  contacts: SearchContactsItem[];
  totalCount: number;
}

export interface WisdomTranscriptMessage {
  messageId: string;
  participant: 'BOT' | 'CUSTOMER';
  timestamp: string;
  value: {
    text: {
      value: string;
    };
  };
}

export interface GetSuggestionsPayload {
  type: 'USER' | 'QUEUE';
  prefix: string;
}

export interface GetSuggestionsResponse {
  message: string;
  type: 'USER' | 'QUEUE';
  results: {
    name: string;
    id: string;
    arn: string;
  }[];
}

export interface GetWisdomTranscriptsPayload {
  assistantId: string;
  sessionId: string;
}

export interface GetWisdomTranscriptsResponse {
  message: string;
  transcript: WisdomTranscriptMessage[];
}

// Wisdom Spans Types
export interface SpanCitation {
  contentId: string;
  title: string;
  knowledgeBaseId: string;
  knowledgeBaseArn?: string;
}

export interface SpanMessageValue {
  text?: {
    value: string;
    citations?: SpanCitation[];
  };
  toolUse?: {
    name: string;
    toolUseId?: string;
    input?: Record<string, unknown>;
  };
}

export interface SpanMessage {
  messageId: string;
  participant: 'CUSTOMER' | 'BOT' | 'AGENT';
  timestamp: string;
  values: SpanMessageValue[];
  citations?: SpanCitation[];
}

export interface SpanAttributes {
  operationName?: string;
  promptType?: string;
  aiAgentName?: string;
  aiAgentType?: string;
  inputMessages?: SpanMessage[];
  outputMessages?: SpanMessage[];
  requestModel?: string;
  usageInputTokens?: number;
  usageOutputTokens?: number;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  toolOutput?: Record<string, unknown>;
  knowledgeBaseId?: string;
  [key: string]: unknown; // Allow additional attributes
}

export interface WisdomSpan {
  spanId: string;
  sessionId: string;
  spanName: string;
  spanType: 'SERVER' | 'CLIENT';
  startTimestamp: string;
  endTimestamp: string;
  status: string;
  requestId: string;
  parentSpanId?: string;
  attributes: SpanAttributes;
}

export interface GetWisdomSpansPayload {
  assistantId: string;
  sessionId: string;
}

export interface GetWisdomSpansResponse {
  message: string;
  spans: WisdomSpan[];
}

type ContactFlowLogsRequest = {
  instanceAlias: string;
  contactId: string;
  kind: 'CONTACT_FLOW';
  startAt: Date | string;
  endAt: Date | string | null;
};

type QAssistantLogsRequest = {
  instanceAlias: string;
  contactId: string;
  kind: 'Q_ASSISTANT';
  sessionId: string;
  startAt: Date | string;
  endAt: Date | string | null;
};

type InteractionLogsRequest = {
  instanceAlias: string;
  contactId: string;
  kind: 'INTERACTION';
  date: string;
};

export type GetRealTimeLogsRequest = ContactFlowLogsRequest | QAssistantLogsRequest | InteractionLogsRequest;

type ContactFlowLogsResult = {
  events: FlowLogEvent[];
  logStreams: string[];
};

type QAssistantLogsResult = {
  events: FlowLogEvent[];
  logStreams: string[];
};

type InteractionLogsResult = {
  object: InteractionLogsObject | null;
};

export interface Configuration {
  companyId: string;
  instanceId: string;
  connect: {
    instanceAlias: string;
    instanceId: string;
    callRecordingBucketName: string;
    callRecordingKeyPrefix: string;
    logGroupName: string;
  };
  wisdom: {
    assistantId: string;
    logGroupName: string;
  };
}

export type GetConfigurationRequest = BaseRequestPayload;
export type GetConfigurationResult = {
  config: Configuration;
};

export type GetRealTimeLogsResult<T extends GetRealTimeLogsRequest['kind']> = T extends 'CONTACT_FLOW'
  ? ContactFlowLogsResult
  : T extends 'Q_ASSISTANT'
    ? QAssistantLogsResult
    : T extends 'INTERACTION'
      ? InteractionLogsResult
      : never;

export type GetRealTimeLogsHandler = <T extends GetRealTimeLogsRequest['kind']>(
  payload: BaseRequestPayload & Extract<GetRealTimeLogsRequest, { kind: T }>
) => Promise<ApiResult<GetRealTimeLogsResult<T>>>;

export interface GetContactResultResponse {
  interactionLogsObject: InteractionLogsObject;
  contact: Contact;
  wisdomMessages: WisdomTranscriptMessage[];
}

export interface GetLambdaMetricsRequest {
  instanceId: string;
  region: string;
  functionName: string;
  startTime: string;
  endTime: string;
}

export interface MetricDataPoint {
  timestamp: string;
  value: number;
}

export interface GetLambdaMetricsResponse {
  message: string;
  data: {
    invocations: MetricDataPoint[];
    errors: MetricDataPoint[];
    duration: MetricDataPoint[];
    concurrentExecutions: MetricDataPoint[];
  };
}

// Get Contact by ID
export type GetContactPayload = BaseRequestPayload & {
  contactId: string;
};

export type GetContactResult = {
  contact: Contact;
};

export class InsightsApiClient extends ApiClientBase {
  async searchContacts(payload: BaseRequestPayload & SearchContactsRequest): Promise<ApiResult<SearchContactsResult>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region
    });

    const url = this.getInsightsApiUrl(`/visualizer/search-contacts?${queryParams}`);
    const result = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({
        contactId: payload.contactId,
        agentIds: payload.agentIds,
        initiationMethods: payload.initiationMethods,
        queueIds: payload.queueIds,
        channels: payload.channels,
        includePending: payload.includePending,
        timeRange: payload.timeRange,
        segmentAttributes: payload.segmentAttributes,
        contactAttributes: payload.contactAttributes
      })
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  // Get Contact by ID
  async getContactById(payload: GetContactPayload): Promise<ApiResult<GetContactResult>> {
    const queryParams = this.formatQueryStrings(payload);
    const result = await fetch(this.getInsightsApiUrl(`/visualizer/contact?${queryParams}`), {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async getConfiguration(payload: BaseRequestPayload): Promise<ApiResult<GetConfigurationResult>> {
    const queryParams = this.formatQueryStrings(payload);
    const result = await fetch(this.getInsightsApiUrl(`/visualizer/config?${queryParams}`), {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async putConfiguration(
    payload: BaseRequestPayload & { configuration: Configuration }
  ): Promise<ApiResult<Configuration>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region
    });

    const url = this.getInsightsApiUrl(`/visualizer/config?${queryParams}`);
    const result = await fetch(url, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload.configuration)
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async getContactResult(
    payload: BaseRequestPayload & {
      contactId: string;
      date: string;
    }
  ): Promise<ApiResult<GetContactResultResponse>> {
    const queryParams = this.formatQueryStrings(payload);
    const result = await fetch(this.getInsightsApiUrl(`/visualizer/get-log?${queryParams}`), {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }
  getRealTimeLogs: GetRealTimeLogsHandler = async (payload) => {
    const queryParams = this.formatQueryStrings(payload);
    const result = await fetch(this.getInsightsApiUrl(`/visualizer/get-realtime-logs?${queryParams}`), {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  };

  async getSuggestions(
    payload: BaseRequestPayload & GetSuggestionsPayload
  ): Promise<ApiResult<GetSuggestionsResponse>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region,
      type: payload.type,
      prefix: payload.prefix
    });

    const url = this.getInsightsApiUrl(`/visualizer/get-suggestions?${queryParams}`);
    const result = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async getWisdomTranscripts(
    payload: BaseRequestPayload & GetWisdomTranscriptsPayload
  ): Promise<ApiResult<GetWisdomTranscriptsResponse>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region,
      assistantId: payload.assistantId,
      sessionId: payload.sessionId
    });

    const url = this.getInsightsApiUrl(`/visualizer/qic/wisdom-transcript?${queryParams}`);
    const result = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async getLambdaMetrics(
    payload: BaseRequestPayload & GetLambdaMetricsRequest
  ): Promise<ApiResult<GetLambdaMetricsResponse>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region,
      functionName: payload.functionName,
      startTime: payload.startTime,
      endTime: payload.endTime
    });

    const url = this.getInsightsApiUrl(`/visualizer/metrics/lambda?${queryParams}`);
    const result = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }

  async getWisdomSpans(
    payload: BaseRequestPayload & GetWisdomSpansPayload
  ): Promise<ApiResult<GetWisdomSpansResponse>> {
    const queryParams = this.formatQueryStrings({
      instanceId: payload.instanceId,
      region: payload.region,
      assistantId: payload.assistantId,
      sessionId: payload.sessionId
    });

    const url = this.getInsightsApiUrl(`/visualizer/qic/wisdom-spans?${queryParams}`);
    const result = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders()
    });

    if (!result.ok) {
      const body = await result.json();
      console.error(body);
      throw new Error(body.message);
    }

    return result.json();
  }
}
